import React, { useState } from 'react';
import { Button } from './ui/button';
import { Building, Loader2, CheckCircle2, AlertCircle, ShieldCheck } from 'lucide-react';

const BankSetupForm = ({
  onSuccess,
  onSave,       // async (data) => { routingNumber, accountNumber, accountHolderName }
  submitLabel = 'Connect Bank Account',
  initialName = '',
}) => {
  const [holderName,     setHolderName]     = useState(initialName);
  const [routingNumber,  setRoutingNumber]   = useState('');
  const [accountNumber,  setAccountNumber]   = useState('');
  const [confirmAccount, setConfirmAccount]  = useState('');
  const [mandateAccepted, setMandate]        = useState(false);
  const [loading,        setLoading]         = useState(false);
  const [error,          setError]           = useState('');
  const [done,           setDone]            = useState(false);
  const [result,         setResult]          = useState(null);

  const validate = () => {
    if (!holderName.trim())               return 'Account holder name is required.';
    if (!/^\d{9}$/.test(routingNumber))   return 'Routing number must be 9 digits.';
    if (accountNumber.length < 5)         return 'Account number must be at least 5 digits.';
    if (accountNumber !== confirmAccount)  return 'Account numbers do not match.';
    if (!mandateAccepted)                  return 'You must authorise the ACH debit to continue.';
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setError(err); return; }

    setError('');
    setLoading(true);
    try {
      const res = await onSave({ routingNumber, accountNumber, accountHolderName: holderName.trim() });
      setResult(res);
      setDone(true);
      if (onSuccess) onSuccess(res);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to connect bank account. Please check your details and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (done && result) {
    return (
      <div className="flex flex-col items-center text-center py-8 px-4 max-w-sm mx-auto">
        <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mb-4">
          <CheckCircle2 className="h-7 w-7 text-green-600" />
        </div>
        <h3 className="text-base font-semibold mb-1">Bank Account Connected</h3>
        {result.bankName && (
          <p className="text-sm text-muted-foreground mb-1">
            {result.bankName} ···· {result.last4}
          </p>
        )}
        {result.needsVerification && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 text-left">
            <p className="font-medium mb-1">Micro-deposit verification required</p>
            <p className="text-xs">Stripe will send two small deposits to your account within 1–2 business days. Check your bank statement and verify the amounts to activate your account.</p>
            {result.verificationUrl && (
              <a
                href={result.verificationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 text-xs font-medium text-amber-700 underline"
              >
                Verify now →
              </a>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-sm w-full mx-auto">
      {/* Account holder name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name</label>
        <input
          type="text"
          value={holderName}
          onChange={e => setHolderName(e.target.value)}
          placeholder="Full name as it appears on the account"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
        />
      </div>

      {/* Routing number */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Routing Number</label>
        <input
          type="text"
          inputMode="numeric"
          maxLength={9}
          value={routingNumber}
          onChange={e => setRoutingNumber(e.target.value.replace(/\D/g, ''))}
          placeholder="9-digit routing number"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 font-mono"
        />
      </div>

      {/* Account number */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
        <input
          type="password"
          inputMode="numeric"
          value={accountNumber}
          onChange={e => setAccountNumber(e.target.value.replace(/\D/g, ''))}
          placeholder="Account number"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 font-mono"
        />
      </div>

      {/* Confirm account number */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Account Number</label>
        <input
          type="text"
          inputMode="numeric"
          value={confirmAccount}
          onChange={e => setConfirmAccount(e.target.value.replace(/\D/g, ''))}
          placeholder="Re-enter account number"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 font-mono"
        />
      </div>

      {/* Mandate */}
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={mandateAccepted}
          onChange={e => setMandate(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500 flex-shrink-0"
        />
        <span className="text-xs text-gray-600 leading-relaxed">
          I authorise NivasaPro to initiate ACH debits and credits to this account in accordance with the lease agreement. I confirm I am the authorised account holder.
        </span>
      </label>

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      <Button
        className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Connecting…</> : submitLabel}
      </Button>

      <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5" />
        Secured by Stripe · Your account details are encrypted and never stored
      </div>
    </div>
  );
};

export const ConnectedBankCard = ({ bankName, last4, routingLast4, status, onUpdate }) => (
  <div className="flex items-center justify-between p-4 border rounded-xl bg-gray-50">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-white border rounded-lg flex items-center justify-center flex-shrink-0">
        <Building className="h-5 w-5 text-gray-500" />
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-900">{bankName || 'Bank Account'}</p>
        <p className="text-xs text-muted-foreground">
          {routingLast4 && <>Routing ····{routingLast4} · </>}Account ····{last4}
        </p>
      </div>
    </div>
    <div className="flex items-center gap-3 flex-shrink-0">
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
        status === 'verified' || status === 'new'
          ? 'bg-green-100 text-green-700'
          : 'bg-amber-100 text-amber-700'
      }`}>
        {status === 'new' ? 'Verified' : status === 'verified' ? 'Verified' : 'Pending'}
      </span>
      {onUpdate && (
        <button onClick={onUpdate} className="text-xs text-cyan-600 hover:underline">
          Update
        </button>
      )}
    </div>
  </div>
);

export default BankSetupForm;
